import { defaultFocusKey, fmtValue, shortLabel, toneOf, type MetricSeries, type TrendRound } from './behaviorTrend';

export const RATIO_KEYS = new Set(['averaging_down', 'fomo_chase', 'falling_knife', 'breakeven_exit', 'no_thesis']);
const PNL_KEYS = new Set(['averaging_down', 'fomo_chase', 'falling_knife', 'no_thesis']);
export const naturalCaption = (key: string): string => ({
  averaging_down: '추가매수 10번 중', fomo_chase: '매수 10번 중', falling_knife: '손실 거래 10번 중',
  breakeven_exit: '매도 10번 중', no_thesis: '매수 10번 중', concentration: '한 종목 비중',
  overtrade: '최근 7일 거래', churn: '평균 보유', no_focus: '보유 종목',
}[key] ?? '측정값');

export function fmtNatural(key: string, value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return RATIO_KEYS.has(key) ? `${Math.round(value / 10)}번` : fmtValue(key, value);
}

export type HabitState = 'loading' | 'failed' | 'empty' | 'first' | 'growing' | 'full';
export function habitState(rounds: TrendRound[], loading = false, failed = false): HabitState {
  if (failed) return 'failed';
  if (loading) return 'loading';
  if (!rounds.length) return 'empty';
  if (rounds.length === 1) return 'first';
  return rounds.length < 6 ? 'growing' : 'full';
}

/** 최신 표본 미달을 과거 값의 개선으로 말하지 않는다. 판정 원값은 buildSeries가 담당한다. */
export const isCurrent = (s: MetricSeries) => s.points[s.points.length - 1] != null;
export function habitTabs(series: MetricSeries[]): MetricSeries[] {
  const key = defaultFocusKey(series, series.length ? 'spark' : 'empty');
  const focus = series.find((s) => s.key === key) ?? series[0];
  return focus ? [focus, ...series.filter((s) => s !== focus && isCurrent(s) &&
    (s.exceeded || s.delta === 'worsened')).slice(0, 2)] : [];
}

/** 주의 = 최신값이 있고 (기준 초과 또는 나빠짐). 설계 §2-18 "기준 초과·나빠진 지표 수" — 기준 안에서 나빠진 것도 센다(막대가 이미 흰색으로 그 사실을 말한다). */
export function needsAttention(s: MetricSeries): boolean {
  return isCurrent(s) && (s.exceeded || s.delta === 'worsened');
}
export const attentionCount = (series: MetricSeries[]) => series.filter(needsAttention).length;
/** 막대 위 "!" 배지 = 나빠졌거나 계속 기준을 넘는 지표. 개선 중인 지표는 아직 기준 위라도 배지를 달지 않는다(금색 막대와 경고가 한 자리에서 싸운다). */
export const showsWarning = (s: MetricSeries): boolean => isCurrent(s) && toneOf(s.delta, s.exceeded) === 'worsened';

export function particle(label: string, kind: 'subject' | 'topic'): string {
  const code = label.charCodeAt(label.length - 1) - 0xac00;
  const final = code >= 0 && code <= 11171 && code % 28 !== 0;
  return kind === 'subject' ? (final ? '이' : '가') : (final ? '은' : '는');
}

export function habitSpeech(s: MetricSeries | undefined, count: number): string {
  if (!count) return '첫 복기 한 번이면 네 습관이 바로 보여. 같이 해볼래?';
  if (!s || !isCurrent(s)) {
    return s?.sample && s.sample.have < s.sample.need
      ? '아직 재기엔 거래가 적어. 조금 더 쌓이면 보여줄게.'
      : '이번 회차에는 이 습관을 측정하지 못했어.';
  }
  const label = shortLabel(s.key, s.label);
  const subject = label + particle(label, 'subject');
  // 보유 기간(direction=higher)은 값이 클수록 건전하다 — 동사가 반대다(길어졌어/짧아졌어 · 기준보다 짧았어).
  //   2026-10-08 정정: 설계 §7 초안이 lower 지표 동사만 적어 Codex 구현이 "보유 기간이 줄었어(=개선)"를 내던 것을 고쳤다.
  const higher = s.direction === 'higher';
  if (count === 1 || s.measuredCount === 1) {
    const firstWord = higher ? (s.exceeded ? '기준보다 짧았어' : '기준만큼 길었어') : (s.exceeded ? '기준보다 많았어' : '기준 안이었어');
    return `첫 복기 잘 봤어. ${subject} ${firstWord}.`;
  }
  const relative = s.first === 0 ? Infinity : Math.abs(((s.last ?? 0) - (s.first ?? 0)) / (s.first ?? 1));
  const sameDisplay = fmtNatural(s.key, s.first) === fmtNatural(s.key, s.last);
  if (s.delta === 'improved') {
    if (!sameDisplay && relative >= 0.4) return higher ? `${subject} 훨씬 길어졌어.` : `${subject} 절반 가까이 줄었어.`;
    return `${subject} 조금 ${higher ? '길어졌어' : '줄었어'}.${count === 2 ? ' 좋은 시작이야.' : ''}`;
  }
  if (s.delta === 'worsened') {
    // 첫 값 0은 배수 비교가 불가능하다. 같은 표기인 작은 변화도 "조금"으로 말한다.
    const big = !sameDisplay && s.first !== 0 && relative >= 0.8;
    return higher ? `${subject} ${big ? '절반 가까이' : '조금'} 짧아졌어.` : `${subject} ${big ? '두 배 가까이' : '조금'} 늘었어.`;
  }
  if (s.exceeded) return `${subject} 아직 기준을 넘고 있어.`;
  return `${label}${particle(label, 'topic')} 그대로 잘 지키고 있어.`;
}

export function habitExpression(s: MetricSeries | undefined, count: number) {
  if (!count) return 'surprised' as const;
  if (count === 1) return 'confident' as const;
  if (!s || !isCurrent(s)) return 'focused' as const;
  if (s.delta === 'improved') return 'celebrate' as const;
  return toneOf(s.delta, s.exceeded) === 'worsened' || s.exceeded ? 'focused' as const : 'confident' as const;
}

/** 그래프와 같은 첫 측정·최근 회차 손익. 옛 회차가 없으면 최근 값만 표시한다. */
export function habitPnl(rounds: TrendRound[], key: string) {
  if (!PNL_KEYS.has(key)) return null;
  const metrics = rounds.map((r) => r.metrics.find((m) => m.key === key));
  const usable = (m: typeof metrics[number]) => m?.value != null && m.pnl && m.pnl.n >= 3 &&
    m.pnl.sum != null && Number.isFinite(m.pnl.sum) ? m.pnl.sum : null;
  const last = usable(metrics[metrics.length - 1]);
  if (last == null) return null;
  const firstIndex = metrics.findIndex((m) => m?.value != null);
  return { first: firstIndex < metrics.length - 1 ? usable(metrics[firstIndex]) : null, last };
}

/** 손익은 BULL이다. 원화 단위를 붙이지 않고 시안의 만·억 한 자리로 적는다. */
export function fmtHabitPnl(value: number): string {
  const abs = Math.abs(value);
  const amount = abs >= 1e8 ? `${Math.round(abs / 1e7) / 10}억`
    : abs >= 1e4 ? `${Math.round(abs / 1e3) / 10}만` : Math.round(abs).toLocaleString('ko-KR');
  return `${value < 0 ? '−' : value > 0 ? '+' : ''}${amount}`;
}
